import { useEffect } from 'react';

import { useQuery } from '@apollo/client';
import type { OwnableProduct_ProductType } from '__generated__/graphql-types';

import type { EnrollmentChoices } from 'bundles/enroll-common/components/xdp/withEnrollment';
import googleCertificateExperiments from 'bundles/epic/clients/GoogleCertificate';
import subscriptionPriceUtils from 'bundles/ownable-products-common/utils/subscriptionPriceUtils';
import FindPriceConfigurationByLegacyProductId from 'bundles/payments-common/api/FindPriceConfigurationByLegacyProductIdQuery.graphql';
import type {
  FindPriceConfigurationByLegacyProductIdQuery,
  FindPriceConfigurationByLegacyProductIdQueryVariables,
} from 'bundles/payments-common/api/__generated__/FindPriceConfigurationByLegacyProductIdQuery';
import BillingCycleType from 'bundles/subscriptions-common/common/BillingCycleType';

// As defined in experiment homebase: https://coursera.atlassian.net/wiki/spaces/GROW/pages/5391155238/Experiment+Plan+Pricing+Test+for+High-income+Countries
const INCLUDED_PRICING_CONFIGURATION_IDS = [
  'pc_uqou6s--QP-1HHEGQGTflw',
  'pc_k0EE3_9CRnK1GHmBiFGb7A',
  'pc_H3JcsbmeS0KoIDBJ3WvaeA',
  'pc_jI4cIXRqR_y25SkFLv9Bug',
  'pc_Hu1MC6qfREuLWf3ShTfypA',
  'pc_u9s0x5iqTS-4ps9m5mZZAw',
  'pc_nkL_INr8Tqi7OE2typpteA',
  'pc_kaVv5l9_SRmKJT2nlwQtTA',
  'pc_wLxrItEgTu6Lg03p2r18Lg',
  'pc_wmTDsekoRWykLjRy-uOm-w',
  'pc_cpqUxYTuTkClx0kZDVVv8Q',
  'pc_koKNeI6zTV6_HDDsonx9Gg',
  'pc_O6W_2uRuTPqnZEPxThoaUg',
  'pc_BdS1BsjTSdS7Q7dZwa9dow',
  'pc_aS9t8ksBRdm20j1Lj83V8w',
  'pc_G5_NS_kzTTGgt4z6Ya8IlQ',
  'pc_DGh5hkxnQmWgXVjH-DIesg',
];

export const isHighIncomeCountryPricingTestEnabled = () =>
  googleCertificateExperiments.preview('highIncomeCountryPricingTestVariant') === 'variant1';

export const generateImpressionForHighIncomeCountryPricingTest = () => {
  googleCertificateExperiments.get('highIncomeCountryPricingTestVariant');
};

export const useGenerateEnrollModalImpressionForHighIncomeCountryPricingTest = ({
  enrollmentAvailableChoices,
  courseId,
  s12nId,
  skip,
}: {
  courseId?: string;
  s12nId?: string;
  enrollmentAvailableChoices?: EnrollmentChoices;
  skip?: boolean;
}) => {
  let productItemId;
  let productType;

  if (enrollmentAvailableChoices?.canSubscribeToS12n && s12nId) {
    const availableS12nSubscriptions = enrollmentAvailableChoices.getS12nSubscriptionProducts(s12nId);
    const s12nMonthlySkuId = subscriptionPriceUtils.getSubscriptionSkuId(
      availableS12nSubscriptions ?? [],
      BillingCycleType.MONTHLY
    );
    productItemId = s12nMonthlySkuId;
    productType = 'PRODUCT_TYPE_SPECIALIZATION_SUBSCRIPTION';
  } else if (courseId) {
    productItemId = courseId;
    productType = 'PRODUCT_TYPE_VERIFIED_CERTIFICATE';
  }

  const { data } = useQuery<
    FindPriceConfigurationByLegacyProductIdQuery,
    FindPriceConfigurationByLegacyProductIdQueryVariables
  >(FindPriceConfigurationByLegacyProductId, {
    variables: {
      legacyProductId: {
        productItemId: productItemId || '',
        productType: productType as OwnableProduct_ProductType,
      },
    },
    skip: !productItemId || !productType || skip,
  });

  const priceConfigurationId = data?.OwnableProductQueries?.findLatestByLegacyProductId?.price?.configuration?.id;

  useEffect(() => {
    if (priceConfigurationId && INCLUDED_PRICING_CONFIGURATION_IDS.includes(priceConfigurationId)) {
      generateImpressionForHighIncomeCountryPricingTest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceConfigurationId]);
};
